/**
 * billingSlice — billing step payload (address + payment method).
 */

import type { StateCreator } from 'zustand'

import type { AddressDTO, PaymentMethod } from 'models/purchaseFlowRevamp'
import type { PfStoreState } from '../store'

export interface BillingSliceState {
  invoicingAddress?: AddressDTO
  paymentMethod?: PaymentMethod
  externalReporting?: string
  reference?: string
  /** Owner of the property, shown on the invoice (legacy's billing field). */
  propertyOwner?: string
  ccEmails: string[]
  /**
   * UX-only: has the user added or removed a CC email in this session?
   * NOT persisted and NOT in the autosave fingerprint — it exists so the saved
   * CC prefill can be shown without seeding the slice on mount (§11.1), while
   * still letting the user clear the list without the prefill reappearing.
   */
  ccEmailsTouched?: boolean
}

export interface BillingSliceActions {
  setInvoicingAddress(address: AddressDTO | undefined): void
  setPaymentMethod(method: PaymentMethod | undefined): void
  setExternalReporting(text: string | undefined): void
  setReference(text: string | undefined): void
  setPropertyOwner(text: string | undefined): void
  /** UX-only marker; see `ccEmailsTouched`. Never dirties the draft. */
  setCcEmailsTouched(touched: boolean): void
  setCcEmails(emails: string[]): void
}

export type BillingSlice = BillingSliceState & BillingSliceActions

const initialState: BillingSliceState = {
  invoicingAddress: undefined,
  paymentMethod: undefined,
  externalReporting: undefined,
  reference: undefined,
  propertyOwner: undefined,
  ccEmails: [],
  ccEmailsTouched: undefined,
}

export const createBillingSlice: StateCreator<PfStoreState, [], [], BillingSlice> = (set) => ({
  ...initialState,

  setInvoicingAddress: (invoicingAddress) => set((state) => ({
    invoicingAddress,
    draft: { ...state.draft, dirty: true },
  })),

  setPaymentMethod: (paymentMethod) => set((state) => ({
    paymentMethod,
    draft: { ...state.draft, dirty: true },
  })),

  setExternalReporting: (externalReporting) => set((state) => ({
    externalReporting,
    draft: { ...state.draft, dirty: true },
  })),

  setPropertyOwner: (propertyOwner) => set((state) => ({
    propertyOwner,
    draft: { ...state.draft, dirty: true },
  })),

  /** Marks the CC list as user-owned. Does NOT dirty the draft — it is UX state. */
  setCcEmailsTouched: (ccEmailsTouched) => set({ ccEmailsTouched }),

  setReference: (reference) => set((state) => ({
    reference,
    draft: { ...state.draft, dirty: true },
  })),

  setCcEmails: (ccEmails) => set((state) => ({
    ccEmails,
    draft: { ...state.draft, dirty: true },
  })),
})
